/**
 * useScheduleHistory.ts
 * A small undo/redo stack for schedule edits (Machine Schedule UX overhaul — M1).
 *
 * Each entry is a pair of thunks: `undo()` reverts the change, `redo()` re-applies
 * it. Callers (drag, keyboard nudge, delete) record an entry right after they
 * commit a mutation. Both thunks should perform the optimistic local update AND
 * persist via the API, so undo/redo are first-class edits.
 *
 * Keyboard: Ctrl/Cmd+Z = undo, Ctrl+Y or Ctrl/Cmd+Shift+Z = redo. Ignored when
 * focus is in a text input / textarea / contenteditable so native editing wins.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface HistoryEntry {
  /** Human label for the snackbar ("Move D-01 → D-02", "Resize Direct Shear", …). */
  label: string;
  undo: () => void | Promise<void>;
  redo: () => void | Promise<void>;
}

interface UseScheduleHistoryResult {
  record: (entry: HistoryEntry) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** Drop all history (e.g. when the date window changes and stacks go stale). */
  clear: () => void;
}

interface UseScheduleHistoryOptions {
  /** True while a drag is active — suppresses keyboard undo/redo mid-gesture. */
  disabled?: boolean;
  /** Surfaced after an undo/redo so the page can show a snackbar. */
  onAfterUndo?: (label: string) => void;
  onAfterRedo?: (label: string) => void;
}

function isEditableTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    el.isContentEditable
  );
}

export function useScheduleHistory(
  opts: UseScheduleHistoryOptions = {},
): UseScheduleHistoryResult {
  const undoStack = useRef<HistoryEntry[]>([]);
  const redoStack = useRef<HistoryEntry[]>([]);
  // Mirror lengths into state so canUndo/canRedo re-render consumers.
  const [counts, setCounts] = useState({ undo: 0, redo: 0 });
  const syncCounts = useCallback(() => {
    setCounts({ undo: undoStack.current.length, redo: redoStack.current.length });
  }, []);

  const record = useCallback((entry: HistoryEntry) => {
    undoStack.current.push(entry);
    redoStack.current = []; // a fresh action invalidates the redo branch
    syncCounts();
  }, [syncCounts]);

  const undo = useCallback(() => {
    const entry = undoStack.current.pop();
    if (!entry) return;
    redoStack.current.push(entry);
    syncCounts();
    void Promise.resolve(entry.undo()).then(() => opts.onAfterUndo?.(entry.label));
  }, [syncCounts, opts]);

  const redo = useCallback(() => {
    const entry = redoStack.current.pop();
    if (!entry) return;
    undoStack.current.push(entry);
    syncCounts();
    void Promise.resolve(entry.redo()).then(() => opts.onAfterRedo?.(entry.label));
  }, [syncCounts, opts]);

  const clear = useCallback(() => {
    undoStack.current = [];
    redoStack.current = [];
    syncCounts();
  }, [syncCounts]);

  // ── keyboard bindings ───────────────────────────────────────────────────────
  const disabled = opts.disabled ?? false;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (disabled) return;
      if (isEditableTarget(e.target)) return;
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [disabled, undo, redo]);

  return {
    record,
    undo,
    redo,
    canUndo: counts.undo > 0,
    canRedo: counts.redo > 0,
    clear,
  };
}
